import { FC, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router";
import cn from "classnames";

import { useAlert, Button, ButtonAppearance, IconName } from "capitalrx-components-2.0/lib/general";
import { UrlQueryParams } from "@/api/interfaces/QueryParams";
import { useResponsive } from "capitalrx-components-2.0/lib/layout";
import { useGetURLQuery } from "capitalrx-components-2.0/lib/hooks";

import styles from "./BackButton.module.scss";

interface UrlParams {
  client?: string;
}

interface BackButtonProps {
  readonly classNames?: string;
  readonly appearance?: "primary" | "secondary" | "text" | "destructive" | "positive" | ButtonAppearance;
  readonly text?: string;
}

const BackButton: FC<BackButtonProps> = ({ classNames, appearance = "secondary", text = "Back" }: BackButtonProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const showAlert = useAlert();
  const { isMobile } = useResponsive({
    isMobile: { desktop: false, mobile: true },
  });

  const { client }: UrlParams = useParams();

  const failureHandler = useCallback(
    (err: string | Error): void => {
      showAlert({ type: "error", message: typeof err === "string" ? err : err.message, style: "banner" });
    },
    [showAlert],
  );

  const urlParams = useGetURLQuery<UrlQueryParams>(failureHandler);

  const previousPage = () => {
    const currentPage = location;

    const searchParams = new URLSearchParams();

    for (const key of Object.getOwnPropertyNames(urlParams)) {
      searchParams.append(key, urlParams[key]);
    }

    const encodedSearchParams = encodeURIComponent(searchParams.toString());

    if (currentPage.pathname === "/find-best-price/drug-details") {
      navigate({
        pathname: urlParams.group_number ? `/${urlParams.group_number}` : "/",
      });
    }
    if (currentPage.pathname === "/find-best-price/price-list") {
      navigate({
        pathname: "/find-best-price/drug-details",
        search: `?${encodedSearchParams}`,
      });
    }
    if (currentPage.pathname === "/find-best-price/pharmacy-details") {
      navigate({
        pathname: "/find-best-price/price-list",
        search: `?${encodedSearchParams}`,
      });
    }

    // whitelabeled
    if (currentPage.pathname === `/client/${client}/find-best-price/drug-details`) {
      navigate({
        pathname: `/client/${client}`,
      });
    }
    if (currentPage.pathname === `/client/${client}/find-best-price/price-list`) {
      navigate({
        pathname: `/client/${client}/find-best-price/drug-details`,
        search: `?${encodedSearchParams}`,
      });
    }
    if (currentPage.pathname === `/client/${client}/find-best-price/pharmacy-details`) {
      navigate({
        pathname: `/client/${client}/find-best-price/price-list`,
        search: `?${encodedSearchParams}`,
      });
    }
  };

  const backClasses = cn(styles["BackButton"], classNames);
  const iconName = isMobile ? "chevron-left" : "arrow-left";
  const testId = isMobile ? "advantage-back-button-mobile" : "advantage-back-button-desktop";

  return (
    <Button
      appearance={isMobile ? "text" : appearance}
      icon={iconName as IconName}
      iconPosition="left"
      className={backClasses}
      onClick={previousPage}
      testId={testId}>
      {isMobile ? null : text}
    </Button>
  );
};

export default BackButton;
